import React, { useState } from "react";
import { Modal, Stack, Form, Button, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import añadirProducto from "../functions/añadirProducto";
import crearUsuario from "../functions/crearUsuario";
import comprobarCorreoExistente from "../functions/comprobarCorreoExistente";
import escribirLog from "../functions/escribirLog";
import { logBusinessAction } from "../services/AuditService";

function ModalAñadir({
  isModalAñadir,
  setIsModalAñadir,
  actualizarEstadoProductos,
  usuario,
  cecos = [],
  crearConFirebaseUid = false,
  // Cuando es true, el alta se hace por la Cloud Function `crearUsuario`
  // (requisito para los roles con permiso `USUARIOS_EDITAR`: las reglas de
  // Firestore solo permiten `create` en `users` a los administradores).
  usarCrearUsuario = false,
}) {
  // ✅ Estado para manejar los campos del formulario
  const [formData, setFormData] = useState({
    dni: "",
    legajo: "",
    legajo2: "",
    name: "",
    apellidos: "",
    email: "",
    phone: "",
    direccion: "",
    vacaciones: "",
    cargo: "",
    contrato: "",
    recursosHumanos: "",
    supervisor: "",
    cecoId: "",
  });
  const [loading, setLoading] = useState(false); // ✅ Estado para loading

  // ✅ Función para manejar cambios en los inputs
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // ✅ Avisa si el correo ya está registrado en el padrón, pero deja continuar.
  // El alta de trabajadores NUNCA se bloquea por un correo repetido: se avisa,
  // la empresa decide, y el servidor es la autoridad final.
  const confirmarSiCorreoYaRegistrado = async (email, rucEmpresa, dni) => {
    const { existe, fichas } = await comprobarCorreoExistente(email, rucEmpresa);
    if (!existe) return true;

    // Si la ficha con ese correo es la del MISMO DNI, no hay conflicto: se
    // actualizará esa ficha.
    const dniSolicitado = String(dni || "").trim().toUpperCase();
    const conflictos = fichas.filter(
      (f) => String(f?.dni || "").trim().toUpperCase() !== dniSolicitado
    );
    if (conflictos.length === 0) return true;

    const detalle = conflictos
      .map((f) => `DNI ${f?.dni || "(sin DNI)"} — ${f?.name || ""} ${f?.apellidos || ""}`.trim())
      .join("<br>");

    const resultado = await Swal.fire({
      title: "Este correo ya está registrado",
      html: `El correo <b>${email}</b> ya pertenece a otra ficha:<br><br>${detalle}<br><br>¿Deseas crear el usuario de todas formas?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      focusCancel: true,
    });

    return Boolean(resultado?.isConfirmed);
  };

  // ✅ Función para añadir producto con validación básica
  const añadirProductoModal = async () => {
    // ✅ Validación básica en frontend
    if (!formData.dni || !formData.name || !formData.apellidos || !formData.email) {
      alert("Por favor, completa todos los campos obligatorios marcados con *.");
      return;
    }

    setLoading(true); // ✅ Activar loading

    try {
      // ✅ Preparar datos
      const activo = true;
      const rucEmpresa = usuario.ruc;
      const rol = "user";
      const dni = formData.dni.trim().toUpperCase();
      const id = dni;
      const legajo = formData.legajo.trim();
      const legajo2 = formData.legajo2 ? formData.legajo2.trim() : "";
      const name = formData.name.trim().toUpperCase();
      const apellidos = formData.apellidos.trim().toUpperCase();
      const email = formData.email.trim().toLowerCase();
      const phone = formData.phone.trim();
      const direccion = formData.direccion.trim().toUpperCase();
      const vacaciones = formData.vacaciones.trim();
      const cargo = formData.cargo.trim().toUpperCase();
      const contrato = formData.contrato.trim().toUpperCase();
      const recursosHumanos = formData.recursosHumanos.trim().toLowerCase();
      const supervisor = formData.supervisor.trim().toLowerCase();
      const cecoId = formData.cecoId;
      const cecoNombre = cecos.find(c => c.id === cecoId)?.nombre || "";
      const urlImagen = "";

      const infoProducto = {
        activo,
        blacklistActive: false,
        cargo,
        contrato,
        recursosHumanos,
        supervisor,
        rucEmpresa,
        rol,
        id,
        dni,
        legajo,
        legajo2,
        name,
        apellidos,
        email,
        phone,
        direccion,
        vacaciones,
        cecoId,
        cecoNombre,
        ...(crearConFirebaseUid ? { firebaseUid: "" } : {}),
      };

      // ✅ Llamar a la función async y verificar éxito
      let success = false;

      if (usarCrearUsuario) {
        // Aviso previo: si el correo ya está en el padrón se informa, pero el
        // alta se puede completar igual (puede ser un error de captura).
        const continuar = await confirmarSiCorreoYaRegistrado(email, rucEmpresa, dni);
        if (!continuar) {
          setIsModalAñadir(false);
          return;
        }

        // Alta por Cloud Function: la escritura la hace el servidor con el
        // Admin SDK, que aplica la autorización real (permiso granular +
        // empresa + lista negra).
        const resultado = await crearUsuario(infoProducto);
        success = Boolean(resultado?.success);

        if (!success) {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Error",
            text: resultado?.message || "No se pudo crear el usuario.",
            showConfirmButton: false,
            timer: 4500,
          });
        } else {
          const avisos = resultado?.avisos || [];
          Swal.fire({
            position: "top-end",
            icon: avisos.length ? "warning" : "success",
            title: avisos.length
              ? "Usuario creado con observaciones"
              : resultado?.action === "updated"
                ? "Perfil Actualizado"
                : "¡Usuario creado exitosamente!",
            html: avisos.length
              ? `${avisos.map((a) => a?.mensaje).join("<br><br>")}<br><br>${
                  resultado?.message || ""
                }`
              : resultado?.action === "updated"
                ? "El usuario ya existía en el sistema. Se han actualizado sus datos."
                : "Se ha enviado un correo de verificación. El usuario ha sido registrado en el sistema.",
            showConfirmButton: false,
            timer: avisos.length ? 8000 : 4000,
          });
        }
      } else {
        success = await añadirProducto(infoProducto, usuario.email, "users");
      }

      if (success) {
        // Mismo rastro que dejaba la escritura directa: la collection `logs`.
        if (usarCrearUsuario) {
          escribirLog("Usuario Guardado en Firestore", infoProducto, usuario.email);
        }

        // Registrar auditoría Opción B
        await logBusinessAction(
          "CREACION_USUARIO",
          "GestionUsuarios",
          { dniNuevo: dni, emailNuevo: email },
          usuario
        );

        // ✅ Actualizar estado y cerrar modal solo si fue exitoso
        actualizarEstadoProductos("users", rucEmpresa);
        setIsModalAñadir(false);
        setFormData({ // ✅ Limpiar formulario
          dni: "",
          legajo: "",
          name: "",
          apellidos: "",
          email: "",
          phone: "",
          direccion: "",
          vacaciones: "",
          cargo: "",
          contrato: "",
          recursosHumanos: "",
          supervisor: "",
          cecoId: "",
        });
      }
    } catch (error) {
      console.error("Error al añadir usuario:", error);
    } finally {
      setLoading(false); // ✅ Desactivar loading
    }
  };

  return (
    <Modal show={isModalAñadir} onHide={() => setIsModalAñadir(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Añadir Nuevo Usuario</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            {/* ✅ Columna Izquierda */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  DNI <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  id="dni"
                  type="text"
                  placeholder="Ingresa el DNI (8-9 dígitos)"
                  value={formData.dni}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Legajo</Form.Label>
                <Form.Control
                  id="legajo"
                  type="text"
                  placeholder="Ingresa el legajo"
                  value={formData.legajo}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Legajo 2 (Opcional)</Form.Label>
                <Form.Control
                  id="legajo2"
                  type="text"
                  placeholder="Ingresa el legajo 2"
                  value={formData.legajo2}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Nombres <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  id="name"
                  type="text"
                  placeholder="Ingresa los nombres"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Apellidos <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  id="apellidos"
                  type="text"
                  placeholder="Ingresa los apellidos"
                  value={formData.apellidos}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Correo Electrónico <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  id="email"
                  type="email"
                  placeholder="ejemplo@dominio.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Número de Celular</Form.Label>
                <Form.Control
                  id="phone"
                  type="text"
                  placeholder="Ingresa el número de celular"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* ✅ Columna Derecha */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  id="direccion"
                  type="text"
                  placeholder="Ingresa la dirección"
                  value={formData.direccion}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Vacaciones Acumuladas</Form.Label>
                <Form.Control
                  id="vacaciones"
                  type="number"
                  placeholder="Días de vacaciones"
                  value={formData.vacaciones}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Cargo</Form.Label>
                <Form.Control
                  id="cargo"
                  type="text"
                  placeholder="Ingresa el cargo"
                  value={formData.cargo}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Centro de Costo (CECO)</Form.Label>
                <Form.Select
                  id="cecoId"
                  value={formData.cecoId}
                  onChange={handleChange}
                >
                  <option value="">-- Seleccionar CECO --</option>
                  {cecos.map((ceco) => (
                    <option key={ceco.id} value={ceco.id}>
                      {ceco.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Contrato</Form.Label>
                <Form.Control
                  id="contrato"
                  type="text"
                  placeholder="Ej. Empleado, Obrero, Jornal"
                  value={formData.contrato}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Correo de Recursos Humanos</Form.Label>
                <Form.Control
                  id="recursosHumanos"
                  type="email"
                  placeholder="correo@empresa.com"
                  value={formData.recursosHumanos}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Correo del Supervisor</Form.Label>
                <Form.Control
                  id="supervisor"
                  type="email"
                  placeholder="correo@empresa.com"
                  value={formData.supervisor}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setIsModalAñadir(false)} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={añadirProductoModal} disabled={loading}>
          {loading ? "Añadiendo..." : "Añadir Usuario"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalAñadir;
